import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../utils/api'
import toast from 'react-hot-toast'

const ChatLanding = () => {
  const { user, logout } = useAuth()
  const [chatConfigs, setChatConfigs] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchChatConfigs()
  }, [])

  const fetchChatConfigs = async () => {
    try {
      const response = await api.get('/chat')
      setChatConfigs(response.data.chatConfigs)
    } catch (error) {
      console.error('Failed to fetch chat configs:', error)
      toast.error('Failed to load chat configurations')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (chatId, chatName) => {
    if (!window.confirm(`Are you sure you want to delete "${chatName}"?`)) {
      return
    }

    try {
      await api.delete(`/chat/${chatId}`)
      toast.success('Chat configuration deleted successfully')
      fetchChatConfigs()
    } catch (error) {
      console.error('Failed to delete chat config:', error)
      toast.error('Failed to delete chat configuration')
    }
  }

  const handleStartPolling = async (chatId) => {
    try {
      await api.post(`/chat/${chatId}/start-polling`)
      toast.success('Chat polling started')
      fetchChatConfigs()
    } catch (error) {
      console.error('Failed to start polling:', error)
      toast.error('Failed to start chat polling')
    }
  }

  const handleStopPolling = async (chatId) => {
    try {
      await api.post(`/chat/${chatId}/stop-polling`)
      toast.success('Chat polling stopped')
      fetchChatConfigs()
    } catch (error) {
      console.error('Failed to stop polling:', error)
      toast.error('Failed to stop chat polling')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">Loading chat configurations...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white font-sans p-12">
      {/* Main Content Area */}
      <div className="p-6 bg-[#1c1c1c]">
        {/* Chat Cards Grid */}
        <div className="grid grid-cols-2 gap-4">
          {chatConfigs.map((chat) => (
            <div key={chat.id} className="bg-[#323232] border-2 border-white p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="text-lg font-bold tracking-wider uppercase mb-1">
                  {chat.name || 'CHAT CONFIG'}
                </div>
                <div className="text-sm tracking-wider uppercase">
                  {chat.youtubeVideoId ? `VIDEO ID: ${chat.youtubeVideoId}` : 'NO VIDEO ID'}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-1 rounded text-xs font-bold tracking-wider uppercase ${
                    chat.isActive ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                  }`}>
                    {chat.isActive ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Link
                  to={`/chat/${chat.id}/edit`}
                  className="text-sm font-medium tracking-wider uppercase hover:text-red-400 transition-colors"
                >
                  EDIT
                </Link>
                
                <Link
                  to={`/chat/${chat.id}/control`}
                  className="text-sm font-medium tracking-wider uppercase hover:text-red-400 transition-colors"
                >
                  CONTROL
                </Link>
                <Link
                  to={`/chat/${chat.id}/host`}
                  className="text-sm font-medium tracking-wider uppercase hover:text-red-400 transition-colors"
                >
                  HOST
                </Link>
                {chat.isActive ? (
                  <button
                    onClick={() => handleStopPolling(chat.id)}
                    className="text-sm font-medium tracking-wider uppercase hover:text-orange-400 transition-colors"
                  >
                    STOP
                  </button>
                ) : (
                  <button
                    onClick={() => handleStartPolling(chat.id)}
                    className="text-sm font-medium tracking-wider uppercase hover:text-green-400 transition-colors"
                  >
                    START
                  </button>
                )}
                <button
                  onClick={() => handleDelete(chat.id, chat.name)}
                  className="text-sm font-medium tracking-wider uppercase hover:text-red-400 transition-colors"
                >
                  DELETE
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="border-t-8 border-black bg-[#1c1c1c] p-4 flex items-center justify-between">
        <div className="flex items-center gap-12">
          <div className="flex items-center gap-4">
            <div className="text-lg font-bold tracking-wider uppercase">YouTube API</div>
            <div className="text-sm text-gray-400 tracking-wider uppercase">
              {process.env.NODE_ENV === 'development' ? 'Development Mode' : 'Production Mode'}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-lg font-bold tracking-wider uppercase">Real-time Chat</div>
            <div className="text-sm text-gray-400 tracking-wider uppercase">
              Live Comment Polling
            </div>
          </div>
        </div>
        <Link
          to="/chat/new"
          className="w-10 h-10 border-2 text-2xl border-white flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"
        >
          +
        </Link>
      </div>
    </div>
  )
}

export default ChatLanding